import { Edit3, ExternalLink, FolderKanban, Plus, RotateCcw, Users } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { createAdminClient, createAdminClientUser, getAdminClients, resetAdminClientAccess } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { ClientRecord } from "../../lib/types";

export function AdminClientsPage() {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientSlug, setClientSlug] = useState("");
  const [cloudflareOwnerEmail, setCloudflareOwnerEmail] = useState("");
  const [cloudflareBillingUrl, setCloudflareBillingUrl] = useState("");
  const [loginName, setLoginName] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginRole, setLoginRole] = useState<"client" | "viewer">("client");
  const [accessClientId, setAccessClientId] = useState("");
  const [accessName, setAccessName] = useState("");
  const [accessEmail, setAccessEmail] = useState("");
  const [accessRole, setAccessRole] = useState<"client" | "viewer">("client");
  const [resetEmail, setResetEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void getAdminClients().then(setClients);
  }, []);

  useEffect(() => {
    if (!accessClientId && clients[0]?.id) setAccessClientId(clients[0].id);
  }, [accessClientId, clients]);

  const reload = async () => {
    setClients(await getAdminClients());
  };

  const submitClient = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const { client } = await createAdminClient({
        clientName,
        clientEmail,
        clientSlug,
        cloudflareAccountOwnerEmail: cloudflareOwnerEmail,
        cloudflareBillingUrl,
      });
      const loginAccessName = loginName.trim() || clientName.trim();
      const loginAccessEmail = loginEmail.trim() || clientEmail.trim();
      if (client && loginAccessEmail && loginAccessName) {
        await createAdminClientUser({
          clientId: client.id,
          email: loginAccessEmail,
          name: loginAccessName,
          role: loginRole,
          loginNotes: "Created from the Memory Media Vault admin clients page.",
        });
      }
      setClientName("");
      setClientEmail("");
      setClientSlug("");
      setCloudflareOwnerEmail("");
      setCloudflareBillingUrl("");
      setLoginName("");
      setLoginEmail("");
      setLoginRole("client");
      setMessage(
        client
          ? `${client.clientName} was created with portal login access for ${loginAccessEmail}.`
          : "Client was created.",
      );
      await reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to create client.");
    } finally {
      setBusy(false);
    }
  };

  const submitAccess = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await createAdminClientUser({
        clientId: accessClientId,
        email: accessEmail,
        name: accessName,
        role: accessRole,
        loginNotes: "Confirmed from the Memory Media Vault admin clients page.",
      });
      setAccessName("");
      setAccessEmail("");
      setAccessRole("client");
      setMessage(`Client login access was confirmed for ${accessEmail}.`);
      await reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to confirm client login access.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const result = await resetAdminClientAccess({ email: resetEmail });
      setResetEmail("");
      setMessage(result.message);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to reset client access.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Admin Clients"
        title="Manage Memory Hub clients and vault enhancements."
        subtitle="Add or edit client details, Memory Media Vault setup status, billing link references, and support context."
      >
        <a href="#create-client" className="focus-ring inline-flex items-center gap-2 rounded-full bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black">
          <Plus className="size-4" />
          Add Client
        </a>
      </PageHeader>

      <section id="create-client" className="grid gap-6 lg:grid-cols-[1.1fr_.9fr]">
        <form onSubmit={submitClient} className="ivory-card rounded-lg p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <span className="grid size-11 place-items-center rounded-lg bg-ktd-black text-ktd-softGold">
              <Users className="size-5" />
            </span>
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#D94A16]">Create Client</p>
              <h2 className="mt-2 font-display text-3xl font-bold">Client profile and login</h2>
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Field label="Client name" value={clientName} onChange={setClientName} required />
            <Field label="Client email" value={clientEmail} onChange={setClientEmail} type="email" required />
            <Field label="Client slug" value={clientSlug} onChange={setClientSlug} required />
            <Field label="Cloudflare owner email" value={cloudflareOwnerEmail} onChange={setCloudflareOwnerEmail} type="email" />
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich md:col-span-2">
              Cloudflare billing URL
              <input
                value={cloudflareBillingUrl}
                onChange={(event) => setCloudflareBillingUrl(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                placeholder="https://dash.cloudflare.com/..."
              />
            </label>
          </div>
          <div className="mt-6 rounded-lg border border-ktd-gold/20 bg-white p-4">
            <p className="text-sm font-extrabold text-ktd-rich">Client login access</p>
            <p className="mt-2 text-xs font-semibold leading-5 text-ktd-muted">
              If these fields are left blank, portal login access will use the client name and client email above.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <Field label="Login name" value={loginName} onChange={setLoginName} />
              <Field label="Login email" value={loginEmail} onChange={setLoginEmail} type="email" />
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Role
                <select
                  value={loginRole}
                  onChange={(event) => setLoginRole(event.target.value as "client" | "viewer")}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
                >
                  <option value="client">Client</option>
                  <option value="viewer">Viewer</option>
                </select>
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            className="focus-ring mt-6 inline-flex items-center gap-2 rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold disabled:opacity-50"
          >
            <Plus className="size-4" />
            Create Client
          </button>
        </form>

        <div className="grid gap-6">
          <form onSubmit={submitAccess} className="ivory-card rounded-lg p-6 sm:p-8">
            <Users className="size-9 text-[#D94A16]" />
            <h2 className="mt-5 font-display text-3xl font-bold">Confirm Client Login</h2>
            <p className="mt-3 text-sm leading-6 text-ktd-muted">
              Assign or update portal login access for an existing Memory Media Vault client.
            </p>
            <div className="mt-5 grid gap-4">
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Client
                <select
                  value={accessClientId}
                  onChange={(event) => setAccessClientId(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
                  required
                >
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.clientName}
                    </option>
                  ))}
                </select>
              </label>
              <Field label="Login name" value={accessName} onChange={setAccessName} required />
              <Field label="Login email" value={accessEmail} onChange={setAccessEmail} type="email" required />
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Role
                <select
                  value={accessRole}
                  onChange={(event) => setAccessRole(event.target.value as "client" | "viewer")}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
                >
                  <option value="client">Client</option>
                  <option value="viewer">Viewer</option>
                </select>
              </label>
            </div>
            <button
              type="submit"
              disabled={busy || !clients.length}
              className="focus-ring mt-5 inline-flex items-center gap-2 rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold disabled:opacity-50"
            >
              <Plus className="size-4" />
              Confirm Login Access
            </button>
          </form>

          <form onSubmit={submitReset} className="ivory-card rounded-lg p-6 sm:p-8">
            <RotateCcw className="size-9 text-[#D94A16]" />
            <h2 className="mt-5 font-display text-3xl font-bold">Reset Client Access</h2>
            <p className="mt-3 text-sm leading-6 text-ktd-muted">
              This clears active sessions and expires unused login codes for the client user. The client can request a new one-time login code after reset.
            </p>
            <div className="mt-5">
              <Field label="Client user email" value={resetEmail} onChange={setResetEmail} type="email" required />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="focus-ring mt-5 inline-flex items-center gap-2 rounded-full border border-ktd-gold/35 px-5 py-3 text-sm font-extrabold text-ktd-rich disabled:opacity-50"
            >
              <RotateCcw className="size-4" />
              Reset Access
            </button>
            {message && <p className="mt-5 rounded-lg bg-white p-4 text-sm font-bold text-ktd-rich">{message}</p>}
          </form>
        </div>
      </section>

      <section className="grid gap-4">
        {clients.map((client) => {
          const uploadUrl =
            client.uploadUrl ||
            (client.latestEventSlug ? `/upload/${client.clientSlug}/${client.latestEventSlug}` : undefined);
          return (
            <article key={client.id} className="ivory-card rounded-lg p-5">
              <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="flex gap-4">
                  <span className="grid size-12 place-items-center rounded-lg bg-ktd-black text-ktd-softGold">
                    <Users className="size-5" />
                  </span>
                  <div>
                    <h2 className="text-xl font-extrabold">{client.clientName}</h2>
                    <p className="mt-1 text-sm text-ktd-muted">{client.clientEmail}</p>
                    <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-[#D94A16]">
                      {client.clientSlug} - Created {formatDate(client.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/admin/client/${client.id}`} className="focus-ring inline-flex items-center gap-2 rounded-full bg-ktd-black px-4 py-2 text-sm font-extrabold text-ktd-softGold">
                    <Edit3 className="size-4" />
                    Edit Client
                  </Link>
                  <Link to="/admin/memory-hub/vault" className="focus-ring inline-flex items-center gap-2 rounded-full bg-[#0f9f9a] px-4 py-2 text-sm font-extrabold text-white">
                    <FolderKanban className="size-4" />
                    Vault Workspace
                  </Link>
                  {uploadUrl && (
                    <a href={uploadUrl} className="focus-ring inline-flex items-center gap-2 rounded-full border border-ktd-gold/35 px-4 py-2 text-sm font-extrabold">
                      <ExternalLink className="size-4" />
                      Upload Link
                    </a>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
        className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
      />
    </label>
  );
}
